<template>
  <div>
    <div class="d-flex align-center pa-3" style="gap:8px">
      <span class="caption grey--text">
        Les fichiers Excel se déposent dans l'onglet Fichiers (via l'admin Django), puis se rattachent à un plan ici.
      </span>
      <v-spacer />
      <v-btn small color="primary" elevation="0" @click="openCreate">
        <v-icon left small>mdi-plus</v-icon>
        Nouveau plan
      </v-btn>
    </div>

    <v-data-table
      :headers="headers"
      :items="plans"
      :loading="loading"
      class="elevation-0 clickable-rows"
      @click:row="open"
    >
      <template v-slot:[`item.owner_label`]="{ item }">
        <v-icon x-small class="mr-1">{{ scopeIcon(item.scope) }}</v-icon>
        <span class="caption">{{ item.owner_label }}</span>
      </template>
      <template v-slot:[`item.metiers`]="{ item }">
        <span v-if="!item.metiers.length" class="caption grey--text">Tous</span>
        <v-chip v-for="m in item.metiers" :key="m" x-small class="mr-1">{{ m }}</v-chip>
      </template>
      <template v-slot:[`item.status`]="{ item }">
        <v-chip x-small :color="statusColor(item.status)" dark>{{ statusLabel(item.status) }}</v-chip>
      </template>
      <template v-slot:[`item.item_count`]="{ item }">
        <span class="caption">{{ item.item_count }} ({{ item.stocked_count }} stockés)</span>
      </template>
      <template v-slot:[`item.is_active`]="{ item }">
        <v-icon small :color="item.is_active ? 'success' : 'grey'">
          {{ item.is_active ? 'mdi-check-circle' : 'mdi-minus-circle-outline' }}
        </v-icon>
      </template>
      <template v-slot:[`item.imported_at`]="{ item }">
        <span class="caption">{{ formatDate(item.imported_at) }}</span>
      </template>
    </v-data-table>

    <v-dialog v-model="createDialog" max-width="560">
      <v-card>
        <v-card-title class="subtitle-1">Nouveau plan de stock</v-card-title>
        <v-card-text>
          <v-alert v-if="createError" type="error" dense text>{{ createError }}</v-alert>
          <v-text-field v-model="form.name" label="Nom" dense outlined />
          <v-select
            v-model="form.scope"
            :items="scopeChoices"
            label="Propriétaire"
            dense
            outlined
            @change="form.ownerId = null"
          />
          <v-autocomplete
            v-model="form.ownerId"
            :items="ownerChoices"
            item-text="label"
            item-value="id"
            :label="scopeLabel"
            dense
            outlined
          />
          <v-autocomplete
            v-model="form.sourceFile"
            :items="meta.files"
            item-text="name"
            item-value="id"
            label="Fichier source (Excel / CSV)"
            clearable
            dense
            outlined
          />
          <v-select
            v-model="form.metiers"
            :items="meta.metiers"
            item-text="label"
            item-value="slug"
            label="Métiers servis (vide = générique)"
            multiple
            chips
            small-chips
            deletable-chips
            dense
            outlined
          />
          <v-select
            v-model="form.preset"
            :items="presetChoices"
            label="Mapping de colonnes (preset)"
            dense
            outlined
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="createDialog = false">Annuler</v-btn>
          <v-btn color="primary" elevation="0" :loading="creating" :disabled="!canCreate" @click="create">
            Créer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  fetchAdminStockPlans,
  fetchAdminStockPlanMeta,
  createAdminStockPlan,
} from '@/services/adminApi'
import { STOCK_PLAN_STATUS, SCOPE_CHOICES, apiErrorMessage } from './stockPlan'

export default {
  name: 'AdminStockPlansPanel',

  data() {
    return {
      loading: false,
      plans: [],
      meta: { presets: {}, owners: { users: [], teams: [], organisations: [] }, metiers: [], files: [] },
      createDialog: false,
      creating: false,
      createError: null,
      form: { name: '', scope: 'organisation', ownerId: null, sourceFile: null, preset: 'costamagna', metiers: [] },
      scopeChoices: SCOPE_CHOICES,
      headers: [
        { text: 'Nom', value: 'name' },
        { text: 'Propriétaire', value: 'owner_label' },
        { text: 'Métiers', value: 'metiers', sortable: false },
        { text: 'Statut', value: 'status' },
        { text: 'Version', value: 'version', align: 'right' },
        { text: 'Articles', value: 'item_count', align: 'right' },
        { text: 'Importé le', value: 'imported_at' },
        { text: 'Actif', value: 'is_active', align: 'center' },
      ],
    }
  },

  computed: {
    ownerChoices() {
      const key = { organisation: 'organisations', team: 'teams', user: 'users' }[this.form.scope]
      return this.meta.owners[key] || []
    },
    scopeLabel() {
      return (SCOPE_CHOICES.find(c => c.value === this.form.scope) || {}).text
    },
    presetChoices() {
      return Object.keys(this.meta.presets)
    },
    canCreate() {
      return this.form.name.trim() && this.form.ownerId
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    async load() {
      this.loading = true
      try {
        this.plans = await fetchAdminStockPlans()
      } catch (e) {
        this.$store.commit('chat/SHOW_NOTIFICATION', { type: 'error', message: 'Erreur lors du chargement des plans de stock.' })
      } finally {
        this.loading = false
      }
    },
    async openCreate() {
      this.createError = null
      this.form = { name: '', scope: 'organisation', ownerId: null, sourceFile: null, preset: 'costamagna', metiers: [] }
      this.createDialog = true
      try {
        this.meta = await fetchAdminStockPlanMeta()
      } catch (e) {
        this.createError = 'Impossible de charger les options du formulaire.'
      }
    },
    async create() {
      this.creating = true
      this.createError = null
      try {
        const plan = await createAdminStockPlan({
          name: this.form.name.trim(),
          [this.form.scope]: this.form.ownerId,
          source_file: this.form.sourceFile,
          preset: this.form.preset,
          metiers: this.form.metiers,
        })
        this.createDialog = false
        this.$router.push({ name: 'admin_stock_plan_detail', params: { id: plan.id } })
      } catch (e) {
        this.createError = apiErrorMessage(e)
      } finally {
        this.creating = false
      }
    },
    open(item) {
      this.$router.push({ name: 'admin_stock_plan_detail', params: { id: item.id } })
    },
    scopeIcon(scope) {
      return { user: 'mdi-account', team: 'mdi-account-group', organisation: 'mdi-domain' }[scope] || 'mdi-help'
    },
    statusLabel(status) {
      return (STOCK_PLAN_STATUS[status] || {}).label || status
    },
    statusColor(status) {
      return (STOCK_PLAN_STATUS[status] || {}).color || 'grey'
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString('fr-FR') : '—'
    },
  },
}
</script>

<style scoped>
.clickable-rows ::v-deep tbody tr {
  cursor: pointer;
}
</style>
